import type { JobListParams, SortDirection } from '@fieldops/shared'
import {
  JOB_STAGES,
  selectedStages,
  stageLabel,
  stageSelection,
  toggleStage,
} from '../lib/job-stages.js'
import { JOB_GROUPS, type JobGroupBy } from '../lib/job-grouping.js'
import { Icon } from './Icon.js'
import { Popover, PopoverDivider, PopoverHeading, PopoverOption } from './Popover.js'

/**
 * The menus that sit in a list toolbar's button group.
 *
 * Each button says what it is currently doing rather than only what it is for:
 * the sort button reads "Booking date", the group button reads "Client". A
 * toolbar of controls all labelled with their own names makes you open every one
 * to find out why the list looks the way it does.
 *
 * Jobs now uses `SortMenu` alone: its tabs do what `StageFilterMenu` did, and
 * grouping by derived status is the shape of the page rather than a choice
 * (decision 35). The other two are kept for the lists still on loose chips.
 */

type JobStatus = JobListParams['status'][number]

export function StageFilterMenu({
  statuses,
  onChange,
}: {
  statuses: JobStatus[]
  onChange: (statuses: JobStatus[]) => void
}) {
  const chosen = selectedStages(statuses)
  const names = chosen.map(stageLabel)

  return (
    <Popover
      grouped
      width={220}
      icon={<Icon name="filter" className="size-3.5 text-ink-400" />}
      label={names.length === 1 ? names[0] : 'Filter'}
      count={names.length > 1 ? names.length : undefined}
      title={names.length > 0 ? `Showing ${names.join(', ')}` : 'Filter by stage'}
    >
      {() => (
        <div>
          <PopoverHeading
            action={
              chosen.length > 0 ? (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-[10px] font-medium text-ink-500 hover:text-ink-900"
                >
                  Clear
                </button>
              ) : undefined
            }
          >
            Stage
          </PopoverHeading>

          {JOB_STAGES.map((stage) => (
            <PopoverOption
              key={stage.key}
              label={stage.label}
              state={stageSelection(stage.key, statuses)}
              onClick={() => onChange(toggleStage(stage.key, statuses))}
            />
          ))}

          <PopoverDivider />
          <p className="px-2 pb-1 text-[10px] leading-snug text-ink-500">
            Nothing selected shows every stage.
          </p>
        </div>
      )}
    </Popover>
  )
}

export function SortMenu<TField extends string>({
  fields,
  sort,
  dir,
  onChange,
}: {
  fields: ReadonlyArray<{ key: TField; label: string }>
  sort: TField
  dir: SortDirection
  onChange: (next: { sort?: TField; dir?: SortDirection }) => void
}) {
  const current = fields.find((field) => field.key === sort)

  return (
    <Popover
      grouped
      width={200}
      icon={<Icon name="sort" className="size-3.5 text-ink-400" />}
      label={current?.label ?? 'Sort'}
      title={`Sorted by ${current?.label ?? sort}, ${dir === 'desc' ? 'descending' : 'ascending'}`}
    >
      {(close) => (
        <div>
          <PopoverHeading>Sort by</PopoverHeading>
          {fields.map((field) => (
            <PopoverOption
              key={field.key}
              mark="disc"
              label={field.label}
              state={field.key === sort ? 'all' : 'none'}
              onClick={() => {
                onChange({ sort: field.key })
                close()
              }}
            />
          ))}

          <PopoverDivider />
          <PopoverOption
            mark="disc"
            label="Descending"
            state={dir === 'desc' ? 'all' : 'none'}
            onClick={() => onChange({ dir: 'desc' })}
          />
          <PopoverOption
            mark="disc"
            label="Ascending"
            state={dir === 'asc' ? 'all' : 'none'}
            onClick={() => onChange({ dir: 'asc' })}
          />
        </div>
      )}
    </Popover>
  )
}

export function GroupMenu({
  value,
  onChange,
  allCollapsed,
  onToggleAll,
}: {
  value: JobGroupBy
  onChange: (value: JobGroupBy) => void
  allCollapsed: boolean
  onToggleAll: () => void
}) {
  const current = JOB_GROUPS.find((group) => group.key === value)

  return (
    <Popover
      grouped
      width={200}
      icon={<Icon name="group" className="size-3.5 text-ink-400" />}
      label={value === 'none' ? 'Group' : (current?.label ?? 'Group')}
      title={value === 'none' ? 'Group the list' : `Grouped by ${current?.label}`}
    >
      {(close) => (
        <div>
          <PopoverHeading>Group by</PopoverHeading>
          {JOB_GROUPS.map((group) => (
            <PopoverOption
              key={group.key}
              mark="disc"
              label={group.label}
              state={group.key === value ? 'all' : 'none'}
              onClick={() => {
                onChange(group.key)
                close()
              }}
            />
          ))}

          {value === 'none' ? null : (
            <>
              <PopoverDivider />
              <button
                type="button"
                onClick={() => {
                  onToggleAll()
                  close()
                }}
                className="w-full rounded-md px-2 py-1 text-left text-xs text-ink-700 transition-colors hover:bg-ink-50"
              >
                {allCollapsed ? 'Expand all groups' : 'Collapse all groups'}
              </button>
              <p className="px-2 pt-1 pb-1 text-[10px] leading-snug text-ink-500">
                Groups cover this page, not the whole result.
              </p>
            </>
          )}
        </div>
      )}
    </Popover>
  )
}
